/**
 * Экран «Что от меня ждут»: работы, адресованные хозяйству.
 *
 * Один вход в ту же модель данных, что и у экрана кампании. Отдельного,
 * «упрощённого» представления работы для хозяйства нет: агрегат по хозяйству и
 * детализация по контурам считаются из одних контурных фактов, поэтому
 * раскрытие карточки не может показать другое число, чем её заголовок.
 *
 * Клиент не решает, что разрешено. Кнопки прячутся, когда сервер сказал, что
 * прав нет, — но отказ всё равно приходит с сервера, и спрятанная кнопка
 * защитой не считается.
 */

import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api, ApiError } from "../../api/client";
import type {
  BulkReportOut,
  FarmObligationDetail,
  FarmObligationOut,
  MyWorks as MyWorksResponse,
  ObligationContourOut,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useAuth } from "../../auth/context";
import { layout, PageHeader } from "../../components/AppShell";
import { ComboBox } from "../../components/ComboBox";
import { DateField } from "../../components/DateField";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { TextField } from "../../components/TextField";
import { AreaProgress, ProgressRow, progressStyles } from "../../components/Progress";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  EvidenceBadge,
  InlineAlert,
  RequestErrorPanel,
} from "../../components/ui";
import { formatDate } from "../../lib/format";
import { countOf, WORDS } from "../../astra/lib/format";
import { levelPath } from "../../lib/levels";
import features from "../features.module.css";
import styles from "./works.module.css";
import {
  outcomePast,
  roleView,
} from "../verification/verificationModel";
import {
  bulkNote,
  deadlineView,
  farmDenominators,
  farmerHero,
  type FarmerHero,
  progressLabel,
  progressSummary,
  stateView,
  verificationSplit,
} from "./worksModel";
import { defineMessages } from "../../i18n/define";
import { COMMON } from "../../i18n/common";
import { useT } from "../../i18n/hooks";
import { DispatcherHeader, FarmChooser, useFarmOptions } from "./FarmChooser";
import { Checkbox } from "../../astra/ui/forms";

const m = defineMessages({
  title: {
    id: "canonWorks.my.title",
    defaultMessage: "Что от меня ждут",
    description: "Заголовок экрана фермера и последний пункт цепочки навигации",
  },
  farmFallback: {
    id: "canonWorks.my.farmFallback",
    defaultMessage: "хозяйство #{id}",
    description: "Подпись хозяйства без названия; {id} — номер",
  },
  purpose: {
    id: "canonWorks.my.purpose",
    defaultMessage: "Работы кампаний, адресованные хозяйству. Состояние считается снизу вверх из работ на полях и нигде не хранится — раскрытие карточки показывает те же факты, из которых сложен её заголовок.",
    description: "Пояснение под заголовком экрана «Что от меня ждут»",
  },
  loginTitle: {
    id: "canonWorks.my.login.title",
    defaultMessage: "Нужен вход",
    description: "Заголовок панели для гостя на экране «Что от меня ждут»",
  },
  loginText: {
    id: "canonWorks.my.login.text",
    defaultMessage: "Чтение в пилоте открыто, а отчётность — нет: она требует входа, роли и области. Войдите, чтобы увидеть работы своего хозяйства.",
    description: "Текст панели для гостя на экране «Что от меня ждут»; пилот — опытная эксплуатация",
  },
  noScopeTitle: {
    id: "canonWorks.my.noScope.title",
    defaultMessage: "Хозяйство не выбрано",
    description: "Заголовок пустого состояния: у пользователя нет хозяйства в области",
  },
  noScopeDefault: {
    id: "canonWorks.my.noScope.default",
    defaultMessage: "Область не выбрана. Пустой список здесь означает именно это, а не «работ нет».",
    description: "Пояснение к пустому состоянию, когда сервер не прислал своего текста",
  },
  farmLabel: {
    id: "canonWorks.my.filter.farm",
    defaultMessage: "Хозяйство",
    description: "Подпись поля фильтра: чьё хозяйство показано",
  },
  onlyOpen: {
    id: "canonWorks.my.filter.onlyOpen",
    defaultMessage: "только открытые и просроченные",
    description: "Подпись флажка фильтра: скрыть закрытые работы",
  },
  backToOwn: {
    id: "canonWorks.my.filter.backToOwn",
    defaultMessage: "Вернуться к своему хозяйству",
    description: "Кнопка: сбросить выбор чужого хозяйства (менеджер)",
  },
  counts: {
    id: "canonWorks.my.counts",
    defaultMessage: "Открытых: {open}; просроченных: {overdue}. Просрочка считается только там, где у работы есть срок.",
    description: "Строка над списком работ; {open}, {overdue} — числа",
  },
  axisProgress: {
    id: "canonWorks.my.axis.progress",
    defaultMessage: "Выполнение — первая ось: «закончено», «в процессе», «без отчёта». «Без отчёта» здесь означает отсутствие данных, а не «не начато»: молчание работой не является.",
    description: "Пояснение над списком работ: первая из двух шкал на карточках",
  },
  axisCheck: {
    id: "canonWorks.my.axis.check",
    defaultMessage: "Проверка — вторая ось, и она НЕ выводится из первой. «Ждут проверки» — это не «проверку не прошли», а «проверяющий ещё не доехал»; работа без отчёта проверке не подлежит вовсе, потому что предмета суждения нет.",
    description: "Пояснение над списком работ: вторая из двух шкал на карточках",
  },
  recorded: {
    id: "canonWorks.my.recorded",
    defaultMessage: "Записано: {work}",
    description: "Заголовок подтверждения после внесения отчёта; {work} — название работы",
  },
  recordedOnBehalf: {
    id: "canonWorks.my.recordedOnBehalf",
    defaultMessage: "Запись внесена от имени хозяйства и сохранила это в себе.",
    description: "Добавка к подтверждению: отчёт внесён менеджером за хозяйство",
  },
  emptyTitle: {
    id: "canonWorks.my.empty.title",
    defaultMessage: "Открытых работ нет",
    description: "Заголовок пустого списка работ фермера",
  },
  emptyText: {
    id: "canonWorks.my.empty.text",
    defaultMessage: "По этому хозяйству не найдено работ, которых ждут прямо сейчас. Снимите фильтр, чтобы увидеть все — включая уже закрытые.",
    description: "Пояснение к пустому списку работ фермера",
  },
  listFailed: {
    id: "canonWorks.my.listFailed",
    defaultMessage: "Список не загрузился",
    description: "Заголовок ошибки загрузки списка работ",
  },
  heroTitle: {
    id: "canonWorks.my.hero.title",
    defaultMessage: "Что от вас ждут сейчас",
    description: "Заголовок карточки-ответа на экране фермера",
  },
  heroDone: {
    id: "canonWorks.my.hero.done",
    defaultMessage: "Отчёта не ждут: по работам этого списка отчитались все поля. Проверка — необязательная отметка проверяющего поверх отчёта, и она идёт своим ходом.",
    description: "Текст карточки-ответа, когда ждать нечего",
  },
  heroAwaiting: {
    id: "canonWorks.my.hero.awaiting",
    defaultMessage: "{count, plural, one {работа на поле ждёт отчёта} few {работы на полях ждут отчёта} many {работ на полях ждут отчёта} other {работы на полях ждут отчёта}}",
    description: "Подпись справа от крупного числа в карточке-ответе; число стоит перед фразой и в неё не входит",
  },
  heroWorks: {
    id: "canonWorks.my.hero.leadDated",
    defaultMessage: "в {works, plural, one {# работе} few {# работах} many {# работах} other {# работах}}; ближайший срок — {date}, {name}",
    description: "Строка карточки-ответа; {works} — число работ, {date} — дата ближайшего срока, {name} — название работы",
  },
  heroWorksNoDate: {
    id: "canonWorks.my.hero.leadNoDate",
    defaultMessage: "в {works, plural, one {# работе} few {# работах} many {# работах} other {# работах}}; срока нет ни у одной из них",
    description: "Строка карточки-ответа, когда ни у одной работы нет срока; {works} — число работ",
  },
  heroUndated: {
    id: "canonWorks.my.hero.undated",
    defaultMessage: "ещё {count} без срока",
    description: "Добавка к строке карточки-ответа; {count} — сколько работ без срока",
  },
  overdue: {
    id: "canonWorks.my.overdue",
    defaultMessage: "просрочено",
    description: "Бейдж: срок работы прошёл; коротко",
  },
  heroAction: {
    id: "canonWorks.my.hero.action",
    defaultMessage: "Отчитаться: {work}",
    description: "Кнопка карточки-ответа: перейти к карточке работы; {work} — название работы",
  },
  campaignHint: {
    id: "canonWorks.my.card.campaignHint",
    defaultMessage: "Кампания, которая поставила эту работу",
    description: "Подсказка у бейджа с названием кампании на карточке работы",
  },
  window: {
    id: "canonWorks.my.card.window",
    defaultMessage: "окно {from} — {to}",
    description: "Подпись на карточке работы: окно выполнения; {from}, {to} — даты",
  },
  levelLink: {
    id: "canonWorks.my.card.levelLink",
    defaultMessage: "Уровень работы",
    description: "Ссылка с карточки на отдельный экран работы",
  },
  axisFull1: {
    id: "canonWorks.my.card.axisFull1",
    defaultMessage: "Выполнение (ось «отчитались», отчёт принят как факт): закончено {done}, в процессе {inProgress}, без отчёта {noReport} из {total}. «Без отчёта» — это отсутствие данных, а не «не начато».",
    description: "Пояснение на карточке работы; аргументы — числа",
  },
  axisFull2: {
    id: "canonWorks.my.card.axisFull2",
    defaultMessage: "Отметки проверяющего поверх отчёта, без своей доли: подтверждено {verified}, спорят или требуют уточнения {disputed}, без отчёта {noReport} из {total}; непомеченные отчёты приняты фактом без изменений.",
    description: "Пояснение на карточке работы; аргументы — числа",
  },
  axisShort: {
    id: "canonWorks.my.card.axisShort",
    defaultMessage: "Закончено {done}, в процессе {inProgress}, без отчёта {noReport}; подтверждено {verified}, спорят {disputed} — из {total}.",
    description: "Краткая подпись на карточке работы; аргументы — числа",
  },
  lastOnBehalf: {
    id: "canonWorks.my.card.lastOnBehalf",
    defaultMessage: "Последняя запись внесена от имени хозяйства. Автор записи и представляемое хозяйство хранятся раздельно — делегация не переносит авторства.",
    description: "Примечание на карточке работы",
  },
  lastOnBehalfBy: {
    id: "canonWorks.my.card.lastOnBehalfBy",
    defaultMessage: "Последняя запись внесена от имени хозяйства: {author}. Автор записи и представляемое хозяйство хранятся раздельно — делегация не переносит авторства.",
    description: "Примечание на карточке работы; {author} — кто внёс запись",
  },
  dateLabel: {
    id: "canonWorks.my.form.dateLabel",
    defaultMessage: "Дата работы",
    description: "Подпись поля даты в форме отчёта",
  },
  dateExplain: {
    id: "canonWorks.my.form.dateExplain",
    defaultMessage: "Это день, когда работа сделана, а не день, когда о ней записали: сервер хранит их раздельно. Значение по умолчанию — сегодня, и его можно изменить.",
    description: "Пояснение к полю даты в форме отчёта",
  },
  done: {
    id: "canonWorks.my.form.done",
    defaultMessage: "Выполнено",
    description: "Кнопка отчёта: работа выполнена; коротко",
  },
  inProgress: {
    id: "canonWorks.my.form.inProgress",
    defaultMessage: "В процессе",
    description: "Кнопка отчёта: работа начата; коротко",
  },
  noDate: {
    id: "canonWorks.my.form.noDate",
    defaultMessage: "Дата работы не задана целиком — записать отчёт без дня нельзя: «когда» здесь часть утверждения, а не оформление.",
    description: "Предупреждение в форме отчёта, когда дата введена не полностью",
  },
  onBehalfNote: {
    id: "canonWorks.my.form.onBehalf",
    defaultMessage: "Вы отчитываетесь ОТ ИМЕНИ хозяйства {farm} — запись это сохранит.",
    description: "Предупреждение менеджеру в форме отчёта; {farm} — название или #номер хозяйства",
  },
  needRole: {
    id: "canonWorks.my.form.needRole",
    defaultMessage: "Отчётность требует роли хозяйства или менеджера. Чтение открыто всем.",
    description: "Пояснение вместо формы отчёта для пользователя без прав на отчёты",
  },
  rejected: {
    id: "canonWorks.my.form.rejected",
    defaultMessage: "Запись не принята",
    description: "Заголовок ошибки: сервер не принял отчёт; ниже — текст ошибки",
  },
  collapse: {
    id: "canonWorks.my.card.collapse",
    defaultMessage: "Свернуть контуры",
    description: "Кнопка на карточке работы: скрыть таблицу контуров",
  },
  expand: {
    id: "canonWorks.my.card.expand",
    defaultMessage: "Показать контуры",
    description: "Кнопка на карточке работы: открыть таблицу контуров",
  },
  contoursFailed: {
    id: "canonWorks.my.contours.failed",
    defaultMessage: "Контуры не загрузились",
    description: "Заголовок ошибки загрузки контуров работы",
  },
  contoursLoading: {
    id: "canonWorks.my.contours.loading",
    defaultMessage: "Загрузка контуров",
    description: "Заголовок индикатора загрузки контуров работы",
  },
  comboLabel: {
    id: "canonWorks.my.contours.comboLabel",
    defaultMessage: "Контур",
    description: "Подпись поля поиска контура в таблице контуров работы",
  },
  comboPlaceholder: {
    id: "canonWorks.my.contours.comboPlaceholder",
    defaultMessage: "номер контура",
    description: "Подсказка в поле поиска контура",
  },
  comboEmpty: {
    id: "canonWorks.my.contours.comboEmpty",
    defaultMessage: "Такого номера в этой работе нет.",
    description: "Пустой результат поиска контура по номеру",
  },
  comboMore: {
    id: "canonWorks.my.contours.comboMore",
    defaultMessage: "ещё {count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}} — уточните номер",
    description: "Подвал списка подсказок поиска контура; {count} — сколько контуров скрыто",
  },
  comboExplain: {
    id: "canonWorks.my.contours.comboExplain",
    defaultMessage: "Ищет по контурам ЭТОЙ работы — по тем, что уже загружены на экран. Сервер в отборе не участвует, поэтому подсказка и таблица ниже не могут разойтись.",
    description: "Пояснение к полю поиска контура",
  },
  showAllContours: {
    id: "canonWorks.my.contours.showAllContours",
    defaultMessage: "Показать все контуры",
    description: "Кнопка: сбросить выбор одного контура и показать все",
  },
  pickedOne: {
    id: "canonWorks.my.contours.pickedOne",
    defaultMessage: "Показан один контур из {count}. Числа в карточке работы выше при этом не меняются: они считаются по всей работе, а не по отобранной строке.",
    description: "Пояснение при выбранном одном контуре; {count} — сколько контуров в работе",
  },
  caption: {
    id: "canonWorks.my.contours.caption",
    defaultMessage: "Контуры этой работы. «Нет отчёта» — отсутствие данных, а не невыполнение. Уточнение по контуру добавляет новую версию отчёта и не стирает ни исходную запись, ни массовый ввод, который её создал. Колонка «Проверка» — второе, независимое утверждение: при расхождении обе записи остаются, и состояние становится «конфликт».",
    description: "Подпись (для чтения с экрана) таблицы контуров работы",
  },
  colContour: {
    id: "canonWorks.my.contours.colContour",
    defaultMessage: "Контур",
    description: "Заголовок колонки: номер контура",
  },
  colState: {
    id: "canonWorks.my.contours.colState",
    defaultMessage: "Состояние",
    description: "Заголовок колонки: состояние работы на контуре",
  },
  colReport: {
    id: "canonWorks.my.contours.colReport",
    defaultMessage: "Отчёт",
    description: "Заголовок колонки: отчёт хозяйства",
  },
  colCheck: {
    id: "canonWorks.my.contours.colCheck",
    defaultMessage: "Проверка",
    description: "Заголовок колонки: суждение проверяющего",
  },
  colArea: {
    id: "canonWorks.my.contours.colArea",
    defaultMessage: "Площадь",
    description: "Заголовок колонки: площадь контура (узкая числовая колонка)",
  },
  colBy: {
    id: "canonWorks.my.contours.colBy",
    defaultMessage: "Кто внёс",
    description: "Заголовок колонки: автор записи",
  },
  colAction: {
    id: "canonWorks.my.contours.colAction",
    defaultMessage: "Действие",
    description: "Заголовок колонки: кнопки отчёта по контуру",
  },
  shownOf: {
    id: "canonWorks.my.contours.shownOf",
    defaultMessage: "Показано {shown} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}}.",
    description: "Подпись под таблицей контуров; {shown} — сколько показано, {total} — сколько всего",
  },
  showAll: {
    id: "canonWorks.my.contours.showAll",
    defaultMessage: "Показать все",
    description: "Кнопка под таблицей контуров: показать все строки; коротко",
  },
  part: {
    id: "canonWorks.my.contours.part",
    defaultMessage: "{base} · часть #{part}",
    description: "Подпись контура с частью; {base} — номер контура или работы на поле, {part} — номер части",
  },
  workOnField: {
    id: "canonWorks.my.contours.workOnField",
    defaultMessage: "работа на поле #{id}",
    description: "Подпись строки, у которой нет номера контура; {id} — номер работы на поле",
  },
  partOnly: {
    id: "canonWorks.my.contours.partOnly",
    defaultMessage: " · часть #{part}",
    description: "Суффикс номера контура с частью; {part} — номер части",
  },
  onBehalf: {
    id: "canonWorks.my.contours.onBehalf",
    defaultMessage: "от имени",
    description: "Бейдж: запись внесена за хозяйство; коротко",
  },
  onBehalfHint: {
    id: "canonWorks.my.contours.onBehalfHint",
    defaultMessage: "Запись внесена не самим хозяйством",
    description: "Подсказка у бейджа «от имени»",
  },
  awaiting: {
    id: "canonWorks.my.contours.awaiting",
    defaultMessage: "ждёт проверки",
    description: "Пометка в ячейке проверки: отчёт есть, проверяющий ещё не смотрел; коротко",
  },
  authorUnknown: {
    id: "canonWorks.my.contours.authorUnknown",
    defaultMessage: "автор не указан",
    description: "Пометка в ячейке проверки: проверяющий не указан",
  },
  verdictBy: {
    id: "canonWorks.my.contours.verdictBy",
    defaultMessage: "{author} ({role}), {date}",
    description: "Подпись суждения; {author} — проверяющий, {role} — его роль, {date} — дата",
  },
  reason: {
    id: "canonWorks.my.contours.reason",
    defaultMessage: "причина: {note}",
    description: "Причина суждения проверяющего; {note} — его текст",
  },
  login: {
    id: "canonWorks.my.signIn.login",
    defaultMessage: "Логин",
    description: "Подпись поля логина в форме входа",
  },
  password: {
    id: "canonWorks.my.signIn.password",
    defaultMessage: "Пароль",
    description: "Подпись поля пароля в форме входа",
  },
  signInFailed: {
    id: "canonWorks.my.signIn.failed",
    defaultMessage: "Войти не удалось",
    description: "Сообщение под формой входа при ошибке",
  },
});

const ACTOR_PARAM = "actor_id";

/**
 * Сколько контуров показывать сразу при раскрытии работы. У крупного хозяйства
 * их сотни, и каждая строка несёт две кнопки: вывалить всё в DOM значит
 * подвесить экран ровно в тот момент, когда человек его открыл. Остальные не
 * прячутся — их число названо, и раскрываются они одной кнопкой.
 */
const CONTOURS_SHOWN = 50;

/**
 * Якорь карточки работы. Нужен ровно для одного жеста: кнопка ответа уровня
 * ведёт к той работе, о которой этот ответ говорит. Считается из того же
 * списка, который отрисован, поэтому цель кнопки не может оказаться скрытой
 * фильтром — иначе кнопка обещала бы работу, которой на экране нет.
 */
export function workAnchor(id: number): string {
  return `work-${id}`;
}

export function MyWorks() {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const [search, setSearch] = useSearchParams();
  const chosen = search.get(ACTOR_PARAM);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [generation, setGeneration] = useState(0);
  // Итог записи живёт на уровне экрана, а не карточки: закончив работу,
  // карточка уходит из отфильтрованного списка — и ответ сервера ушёл бы
  // вместе с ней, оставив человека без подтверждения того, что он сделал.
  const [lastEntry, setLastEntry] = useState<{
    work: string;
    result: BulkReportOut;
  } | null>(null);

  const params = useMemo(
    () => ({
      ...context.params,
      only_open: onlyOpen,
      ...(chosen ? { actor_id: chosen } : {}),
    }),
    [context.params, chosen, onlyOpen],
  );

  // Вход и выход входят в ключ запроса: экран смотрит от лица вошедшего, и
  // после входа он обязан перечитаться. Без этого человек, только что
  // назвавшийся, продолжал бы видеть ответ, собранный для анонима.
  const identity = auth.principal?.is_authenticated
    ? (auth.principal.username ?? "")
    : "";
  const works = useRequest<MyWorksResponse>(
    () => api.myWorks(params),
    [context.key, chosen ?? "", onlyOpen, generation, identity],
  );
  const reload = useCallback(() => setGeneration((count) => count + 1), []);

  const data = works.data;
  const principal = data?.principal ?? auth.principal;
  const hero = useMemo(() => farmerHero(data?.items ?? []), [data]);

  // Хозяйство встаёт ступенью цепочки, только когда область — ровно одно
  // хозяйство. При нескольких выбрать одно за человека нельзя, а показать все
  // значило бы дать цепочке развилку, которой у уровня не бывает.
  const soleActor = data && data.actors.length === 1 ? data.actors[0] : undefined;

  // Районная область (менеджер, хоким): своего хозяйства нет, экран смотрит
  // глазами выбранного — хозяйство выбирается здесь же и живёт в адресе.
  const dispatcher = Boolean(principal?.district_scope);
  const farmOptions = useFarmOptions(dispatcher);
  const pickFarm = useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(search);
      if (id) next.set(ACTOR_PARAM, id);
      else next.delete(ACTOR_PARAM);
      setSearch(next, { replace: true });
    },
    [search, setSearch],
  );
  const dispatcherFarm = chosen
    ? {
        id: chosen,
        name:
          farmOptions.options.find((option) => option.id === chosen)?.name ??
          soleActor?.name ??
          t(m.farmFallback, { id: chosen }),
      }
    : null;

  return (
    <div className={features.stack}>
      {dispatcher ? (
        <DispatcherHeader
          farm={dispatcherFarm}
          options={farmOptions.options}
          optionsFailed={farmOptions.failed}
          canEnter={Boolean(principal?.can_report)}
          onPick={pickFarm}
        />
      ) : (
        <PageHeader
          title={t(m.title)}
          breadcrumb={
            <LevelTrail
              steps={
                soleActor
                  ? [
                      districtStep(context.linkSearch()),
                      {
                        level: "actor",
                        label: soleActor.name ?? t(m.farmFallback, { id: soleActor.id }),
                        to: {
                          pathname: levelPath("actor", soleActor.id),
                          search: context.linkSearch(),
                        },
                      },
                    ]
                  : [districtStep(context.linkSearch())]
              }
              screen={t(m.title)}
            />
          }
          purpose={t(m.purpose)}
        />
      )}

      {principal && !principal.is_authenticated ? (
        <DataStatePanel
          state="blocked"
          title={t(m.loginTitle)}
          action={<SignInForm />}
        >
          {t(m.loginText)}
        </DataStatePanel>
      ) : null}

      {dispatcher && !chosen ? (
        <FarmChooser canEnter={Boolean(principal?.can_report)} onPick={pickFarm} />
      ) : null}

      {!dispatcher && data?.no_scope ? (
        <DataStatePanel state="empty" title={t(m.noScopeTitle)}>
          {data.scope_note ?? t(m.noScopeDefault)}
        </DataStatePanel>
      ) : null}

      {data && !data.no_scope ? (
        <>
          <div className={layout.filterBar}>
            {dispatcher ? null : (
              <div className={layout.field}>
                <span className={layout.fieldLabel}>{t(m.farmLabel)}</span>
                <span>
                  {data.actors
                    .map((actor) => actor.name ?? t(m.farmFallback, { id: actor.id }))
                    .join(", ")}
                </span>
              </div>
            )}
            <div className={layout.field}>
              <Checkbox isSelected={onlyOpen} onChange={setOnlyOpen}>
                {t(m.onlyOpen)}
              </Checkbox>
            </div>
            {chosen && !dispatcher ? (
              <Button
                onClick={() => {
                  const next = new URLSearchParams(search);
                  next.delete(ACTOR_PARAM);
                  setSearch(next, { replace: true });
                }}
              >
                {t(m.backToOwn)}
              </Button>
            ) : null}
          </div>

          <FarmerHeroCard hero={hero} />

          <p className={styles.hint}>
            {t(m.counts, { open: data.open_count, overdue: data.overdue_count })}
          </p>

          {/* Два абзаца про оси стоят ЗДЕСЬ, один раз на экран, и выключены в
              карточках. Тот же текст, напечатанный в каждой из десятка
              карточек, перестают читать после второй — и вместе с ним
              перестают читать карточку. */}
          {data.items.length > 0 ? (
            <>
              <p className={styles.hint}>{t(m.axisProgress)}</p>
              <p className={styles.hint}>{t(m.axisCheck)}</p>
            </>
          ) : null}

          {lastEntry ? (
            <InlineAlert title={t(m.recorded, { work: lastEntry.work })}>
              {bulkNote(lastEntry.result)}
              {lastEntry.result.entered_on_behalf
                ? ` ${t(m.recordedOnBehalf)}`
                : ""}
            </InlineAlert>
          ) : null}

          {data.items.length === 0 ? (
            <DataStatePanel state="empty" title={t(m.emptyTitle)}>
              {t(m.emptyText)}
            </DataStatePanel>
          ) : null}

          {data.items.map((item) => (
            <div key={item.id} id={workAnchor(item.id)}>
              <WorkCard
                item={item}
                axisNotes={false}
                onChanged={reload}
                onReported={(result) =>
                  setLastEntry({ work: item.work_name, result })
                }
              />
            </div>
          ))}
        </>
      ) : null}

      {works.error && !data ? (
        <RequestErrorPanel
          error={works.error}
          title={t(m.listFailed)}
          action={<Button onClick={works.reload}>{t(COMMON.retry)}</Button>}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Ответ уровня: что от хозяйства ждут прямо сейчас                    */
/* ------------------------------------------------------------------ */

/**
 * Одно число, один срок, одно действие.
 *
 * Всё остальное на экране — подробность: пояснения про оси, про молчание и
 * про то, как считается просрочка, стоят НИЖЕ. Первый взгляд отвечает на
 * вопрос «что от меня ждут», а не объясняет, как устроен ответ.
 *
 * Считается «без отчёта», а не «не выполнено»: молчание — отсутствие данных.
 */
export function FarmerHeroCard({ hero }: { hero: FarmerHero }) {
  const t = useT();
  if (hero.awaiting === 0) {
    return (
      <Card title={t(m.heroTitle)} large>
        <p className={styles.heroLead}>{t(m.heroDone)}</p>
      </Card>
    );
  }

  const jump = () => {
    if (!hero.next) return;
    const node = document.getElementById(workAnchor(hero.next.id));
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "start" });
    }
  };

  return (
    <Card title={t(m.heroTitle)} large>
      <div className={styles.hero}>
        <p className={styles.heroCount}>
          <Count value={hero.awaiting} />{" "}
          <span className={styles.heroUnit}>
            {t(m.heroAwaiting, { count: hero.awaiting })}
          </span>
        </p>
        <p className={styles.heroLead}>
          {hero.next?.windowTo ? (
            <>
              {t(m.heroWorks, {
                works: hero.works,
                date: formatDate(hero.next.windowTo),
                name: hero.next.workName,
              })}{" "}
              {hero.next.overdue ? (
                <Badge tone="attention">{t(m.overdue)}</Badge>
              ) : null}
            </>
          ) : (
            t(m.heroWorksNoDate, { works: hero.works })
          )}
          {hero.next?.windowTo && hero.undated > 0
            ? `; ${t(m.heroUndated, { count: hero.undated })}`
            : ""}
        </p>
        {hero.next ? (
          <Button variant="primary" onClick={jump}>
            {t(m.heroAction, { work: hero.next.workName })}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Карточка одной работы                                               */
/* ------------------------------------------------------------------ */

export function WorkCard({
  item,
  onChanged,
  onReported,
  axisNotes = true,
}: {
  item: FarmObligationOut;
  onChanged: () => void;
  onReported: (result: BulkReportOut) => void;
  /**
   * Печатать ли на карточке два абзаца про оси.
   *
   * По умолчанию — да: карточка появляется поодиночке, и без объяснения
   * «проверено 0» рядом с «отчитались 27» читается как провал проверки.
   * Экран, где карточек десяток, объясняет это ОДИН раз у себя и выключает
   * повтор: один и тот же абзац, напечатанный десять раз, перестают читать
   * после второго — и вместе с ним перестают читать карточку.
   */
  axisNotes?: boolean;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const state = stateView(item.state);
  const deadline = deadlineView(item);
  const progress = progressSummary(item.by_progress);
  const checks = verificationSplit(item);
  const canReport = auth.principal?.can_report ?? false;
  const onBehalf = !(auth.principal?.actors ?? []).some(
    (actor) => actor.id === item.actor.id,
  );

  // Дата работы живёт на карточке, а не в кнопке: контурные уточнения ниже
  // относятся к тому же выезду, и два разных дня в одной записи о работе были
  // бы двумя разными утверждениями о том, когда её сделали.
  const [occurredOn, setOccurredOn] = useState<string | null>(today());

  const report = async (value: string) => {
    if (occurredOn === null) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await api.reportFarmObligation(
        item.id,
        { progress: value, occurred_on: occurredOn },
        context.params,
      );
      onReported(answer);
      onChanged();
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={
        <>
          {item.work_name} <EvidenceBadge status={item.epistemic_status} />
        </>
      }
      large
    >
      <div className={features.rowWrap}>
        <Badge title={t(m.campaignHint)}>
          {item.campaign_title}
        </Badge>
        <Badge tone={state.attention ? "attention" : "neutral"}>{state.label}</Badge>
        <Badge tone={deadline.overdue ? "attention" : "neutral"}>
          {deadline.label}
        </Badge>
        <span className={styles.hint}>
          {t(m.window, {
            from: formatDate(item.window_from),
            to: formatDate(item.window_to),
          })}
        </span>
        {/* Спуск на уровень работы. Карточка отвечает на вопрос «что от меня
            ждут» и остаётся местом ВВОДА; уровень работы отвечает на вопрос
            «что это за работа и что по ней записано» — и у него есть адрес,
            который можно скопировать и дать другому человеку. */}
        <Link
          to={{
            pathname: levelPath("work", item.id),
            search: context.linkSearch(),
          }}
        >
          {t(m.levelLink)}
        </Link>
      </div>

      {state.hint ? <p className={styles.hint}>{state.hint}</p> : null}
      {deadline.hint ? <p className={styles.hint}>{deadline.hint}</p> : null}

      {axisNotes ? (
        <>
          <p className={styles.hint}>
            {t(m.axisFull1, {
              done: progress.done,
              inProgress: progress.inProgress,
              noReport: progress.noReport,
              total: item.obligations.total,
            })}
          </p>

          <p className={styles.hint}>
            {t(m.axisFull2, {
              verified: checks.verified,
              disputed: checks.disputed,
              noReport: checks.noReport,
              total: item.obligations.total,
            })}
          </p>
        </>
      ) : (
        <p className={styles.hint}>
          {t(m.axisShort, {
            done: progress.done,
            inProgress: progress.inProgress,
            noReport: progress.noReport,
            verified: checks.verified,
            disputed: checks.disputed,
            total: item.obligations.total,
          })}
        </p>
      )}

      <div className={progressStyles.progressGrid}>
        {farmDenominators(item).map((view) => (
          <ProgressRow key={view.label} view={view} />
        ))}
        <AreaProgress area={item.area} />
      </div>

      {item.last_entry_on_behalf ? (
        <p className={styles.onBehalf}>
          {item.last_entry_by
            ? t(m.lastOnBehalfBy, { author: item.last_entry_by })
            : t(m.lastOnBehalf)}
        </p>
      ) : null}

      {canReport ? (
        /* Форма отчёта: КОГДА и ЧТО, в этом порядке.
           Раньше день работы подставлялся сегодняшним молча, и отчёт о вчерашнем
           поливе ложился в базу сегодняшним числом — то есть утверждал неправду
           про календарь. Дата теперь названа и правится; «сегодня» осталось
           значением по умолчанию, а не подстановкой. */
        <div className={styles.report}>
          <DateField
            label={t(m.dateLabel)}
            size="sm"
            value={occurredOn}
            onChange={setOccurredOn}
            explain={t(m.dateExplain)}
          />
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={busy || occurredOn === null}
              onClick={() => void report("done")}
            >
              {t(m.done)}
            </Button>
            <Button
              disabled={busy || occurredOn === null}
              onClick={() => void report("in_progress")}
            >
              {t(m.inProgress)}
            </Button>
          </div>
          {occurredOn === null ? (
            <p className={styles.hint}>{t(m.noDate)}</p>
          ) : null}
          {onBehalf ? (
            <span className={styles.onBehalf}>
              {t(m.onBehalfNote, { farm: item.actor.name ?? `#${item.actor.id}` })}
            </span>
          ) : null}
        </div>
      ) : (
        <p className={styles.hint}>{t(m.needRole)}</p>
      )}

      {error ? (
        <InlineAlert title={t(m.rejected)}>{error.message}</InlineAlert>
      ) : null}

      <div className={styles.actions}>
        <Button onClick={() => setExpanded((value) => !value)}>
          {expanded ? t(m.collapse) : t(m.expand)}
        </Button>
        <span className={styles.hint}>
          {countOf(item.obligations.total, WORDS.obligation)}
        </span>
      </div>

      {expanded ? (
        <ContourTable
          farmObligationId={item.id}
          occurredOn={occurredOn}
          onChanged={onChanged}
        />
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Раскрытие по контурам                                               */
/* ------------------------------------------------------------------ */

/**
 * Сколько контуров показывать в подсказке выбора. Список подсказок — не
 * реестр: он существует, чтобы найти один контур по номеру, а не чтобы читать
 * их все.
 */
const CONTOUR_HINTS = 8;

export function ContourTable({
  farmObligationId,
  occurredOn,
  onChanged,
  readOnly = false,
}: {
  farmObligationId: number;
  /**
   * День работы для уточнений по контурам. Приходит с карточки работы: и
   * массовый отчёт, и поштучное уточнение говорят про ОДИН выезд, и две
   * разные даты в них были бы двумя разными утверждениями о том, когда
   * работу сделали. `null` — дата не задана, действия недоступны.
   */
  occurredOn?: string | null;
  onChanged?: () => void;
  /**
   * Только записи, без действий.
   *
   * Нужно экрану истории: там те же контурные факты читаются как ПРОТОКОЛ —
   * что сказало хозяйство и что ответила проверка. Кнопка «выполнено» в
   * протоколе означала бы, что запись прошлого можно поправить задним числом,
   * а её здесь и не поправляют: уточнение добавляет новую версию отчёта на
   * экране, где отчитываются.
   */
  readOnly?: boolean;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const [generation, setGeneration] = useState(0);
  const [showAll, setShowAll] = useState(false);
  // Выбор одного контура. Держится ключом обязательства, а не номером контура:
  // номер бывает пустым и бывает повторяющимся между частями, а ключ строки —
  // нет.
  const [picked, setPicked] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const detail = useRequest<FarmObligationDetail>(
    () => api.farmObligation(farmObligationId, context.params),
    [context.key, farmObligationId, generation],
  );

  const refresh = () => {
    setGeneration((count) => count + 1);
    onChanged?.();
  };

  if (detail.error && !detail.data) {
    return (
      <RequestErrorPanel
        error={detail.error}
        title={t(m.contoursFailed)}
        action={<Button onClick={detail.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!detail.data) {
    return <DataStatePanel state="loading" title={t(m.contoursLoading)} />;
  }

  const contours = detail.data.contours;
  const label = (row: ObligationContourOut) => {
    const base = row.contour_no ?? t(m.workOnField, { id: row.obligation_id });
    return row.contour_part_id
      ? t(m.part, { base, part: row.contour_part_id })
      : base;
  };

  // Отбор идёт по УЖЕ загруженному списку этой работы, и владелец списка —
  // этот компонент. Сервер здесь не участвует, поэтому подсказка и таблица не
  // могут показать разные выборки: они читают один массив.
  const needle = query.trim().toLowerCase();
  const hints =
    needle === ""
      ? contours.slice(0, CONTOUR_HINTS)
      : contours
          .filter((row) => label(row).toLowerCase().includes(needle))
          .slice(0, CONTOUR_HINTS);
  const hintsHidden = Math.max(
    0,
    (needle === ""
      ? contours.length
      : contours.filter((row) => label(row).toLowerCase().includes(needle))
          .length) - hints.length,
  );

  const selected =
    picked === null
      ? contours
      : contours.filter((row) => row.obligation_id === picked);
  const shown = showAll ? selected : selected.slice(0, CONTOURS_SHOWN);

  return (
    <div className={features.stack}>
      {/* Найти один контур из сотни набором номера, а не прокруткой таблицы.
          До этого единственным способом добраться до нужной строки было
          «Показать все» и поиск глазами. */}
      <div className={layout.filterBar}>
        <ComboBox<ObligationContourOut>
          label={t(m.comboLabel)}
          size="sm"
          placeholder={t(m.comboPlaceholder)}
          inputValue={query}
          onInputChange={(next) => {
            setQuery(next);
            if (next.trim() === "" && picked !== null) setPicked(null);
          }}
          items={hints}
          itemKey={(row) => String(row.obligation_id)}
          itemLabel={label}
          selectedKey={picked === null ? null : String(picked)}
          onSelect={(row) => setPicked(row ? row.obligation_id : null)}
          emptyState={t(m.comboEmpty)}
          footer={
            hintsHidden > 0
              ? t(m.comboMore, { count: hintsHidden })
              : null
          }
          explain={t(m.comboExplain)}
        />
        {picked !== null ? (
          <Button
            onClick={() => {
              setPicked(null);
              setQuery("");
            }}
          >
            {t(m.showAllContours)}
          </Button>
        ) : null}
      </div>

      {picked !== null ? (
        <p className={styles.hint}>{t(m.pickedOne, { count: contours.length })}</p>
      ) : null}

    <div className={features.scrollX}>
      <table className={features.plainTable}>
        <caption>{t(m.caption)}</caption>
        <thead>
          <tr>
            <th scope="col">{t(m.colContour)}</th>
            <th scope="col">{t(m.colState)}</th>
            <th scope="col">{t(m.colReport)}</th>
            <th scope="col">{t(m.colCheck)}</th>
            <th scope="col" className={features.num}>
              {t(m.colArea)}
            </th>
            <th scope="col">{t(m.colBy)}</th>
            {readOnly ? null : <th scope="col">{t(m.colAction)}</th>}
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => (
            <ContourRow
              key={row.obligation_id}
              row={row}
              canReport={!readOnly && (auth.principal?.can_report ?? false)}
              readOnly={readOnly}
              occurredOn={occurredOn}
              onChanged={refresh}
            />
          ))}
        </tbody>
      </table>
      {selected.length > CONTOURS_SHOWN ? (
        <p className={styles.hint}>
          {t(m.shownOf, { shown: shown.length, total: selected.length })}{" "}
          {showAll ? null : (
            <Button onClick={() => setShowAll(true)}>{t(m.showAll)}</Button>
          )}
        </p>
      ) : null}
    </div>
    </div>
  );
}

function ContourRow({
  row,
  canReport,
  occurredOn,
  onChanged,
  readOnly = false,
}: {
  row: ObligationContourOut;
  canReport: boolean;
  /** День работы с карточки. `null` — дата не задана, записывать нечего. */
  occurredOn?: string | null;
  onChanged: () => void;
  readOnly?: boolean;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const state = stateView(row.state);
  const progress = progressLabel(row.progress);
  // Дата приходит сверху; `today()` остаётся запасным значением ровно для
  // того случая, когда карточки над таблицей нет вовсе.
  const when = occurredOn === undefined ? today() : occurredOn;

  const send = async (value: string) => {
    if (when === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.reportObligation(
        row.obligation_id,
        // amend — только когда есть что уточнять: иначе это новое утверждение,
        // а не исправление прежнего.
        { progress: value, occurred_on: when, amend: row.progress !== null },
        context.params,
      );
      onChanged();
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <th scope="row">
        {row.contour_no}
        {row.contour_part_id ? t(m.partOnly, { part: row.contour_part_id }) : ""}
      </th>
      <td>
        <Badge tone={state.attention ? "attention" : "neutral"}>{state.label}</Badge>
      </td>
      <td>
        {progress.label}
        {row.reported_on ? ` · ${formatDate(row.reported_on)}` : ""}
      </td>
      <td>
        <ContourVerdict row={row} />
      </td>
      <td className={features.num}>
        <Area microha={row.area_microha} />
      </td>
      <td>
        {row.reported_by ?? "—"}
        {row.entered_on_behalf ? (
          <>
            {" "}
            <Badge tone="attention" title={t(m.onBehalfHint)}>
              {t(m.onBehalf)}
            </Badge>
          </>
        ) : null}
      </td>
      {readOnly ? null : (
        <td>
          {canReport ? (
            <div className={styles.actions}>
              <Button
                disabled={busy || when === null}
                onClick={() => void send("done")}
              >
                {t(m.done)}
              </Button>
              <Button
                disabled={busy || when === null}
                onClick={() => void send("in_progress")}
              >
                {t(m.inProgress)}
              </Button>
            </div>
          ) : (
            "—"
          )}
          {error ? <p className={styles.hint}>{error.message}</p> : null}
        </td>
      )}
    </tr>
  );
}

/**
 * Ячейка «Проверка»: что сказал проверяющий — рядом с тем, что сказало
 * хозяйство, а не вместо него. Пусто здесь означает «суждения нет», и это
 * НЕ «проверку не прошли»: последнее называется опровержением и написано
 * словом.
 */
function ContourVerdict({ row }: { row: ObligationContourOut }) {
  const t = useT();
  if (row.verifications.length === 0) {
    return (
      <span className={styles.hint}>
        {row.awaiting_verification ? t(m.awaiting) : "—"}
      </span>
    );
  }
  return (
    <>
      {row.verifications.map((claim) => {
        const said = outcomePast(claim.outcome);
        const who = roleView(claim.verifier_role);
        return (
          <div key={claim.verification_id}>
            <Badge
              tone={said.attention ? "attention" : "neutral"}
              title={claim.note || undefined}
            >
              {said.label}
            </Badge>{" "}
            <span className={styles.hint}>
              {t(m.verdictBy, {
                author: claim.verifier ?? t(m.authorUnknown),
                role: who.label,
                date: formatDate(claim.occurred_on),
              })}
            </span>
            {claim.note ? (
              <div className={styles.hint}>{t(m.reason, { note: claim.note })}</div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Форма входа                                                         */
/* ------------------------------------------------------------------ */

export function SignInForm() {
  const t = useT();
  const auth = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(username, password);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : t(m.signInFailed),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className={styles.signIn} onSubmit={submit}>
      <div className={styles.signInField}>
        <TextField
          label={t(m.login)}
          value={username}
          autoComplete="username"
          onChange={setUsername}
          isRequired
        />
      </div>
      <div className={styles.signInField}>
        <TextField
          label={t(m.password)}
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={setPassword}
          isRequired
        />
      </div>
      <Button type="submit" variant="primary" disabled={busy}>
        {t(COMMON.signIn)}
      </Button>
      {error ? <span className={styles.hint}>{error}</span> : null}
    </form>
  );
}

/** Дата работы по умолчанию — сегодня, и это утверждение автора, не догадка. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}
